import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { endOfDay, format as formatDate, isThisYear } from 'date-fns'

import { ChevronLeftIcon } from '@gto/icons-react/outline/chevron-left'
import { CalendarIcon } from '@gto/icons-react/solid/calendar'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Drawer, type DrawerLayer } from '@gto/ui/drawer'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { IconButton } from '@gto/ui/icon-button'
import { InputGroup, inputVariants } from '@gto/ui/input'
import { Popover } from '@gto/ui/popover'
import { Typography, typography } from '@gto/ui/typography'

import { Calendar } from './calendar'
import { DateFieldInput } from './date-field-input'
import { type DateRangePreset, DateRangePresets, type DateRangeValue } from './date-range-presets'
import { validateBounds, type ValidationError } from './date-segments'
import { useCalendarPopover } from './use-calendar-popover'
import { useDateSegments } from './use-date-segments'

export type { DateRangePreset, DateRangeValue }

export type DateRangeValidationError = [ValidationError | null, ValidationError | null]

export type DateRangePickerProps = {
  value: DateRangeValue
  /**
   * Emitted on every edit, with the per-field validation pair.
   *
   * An inverted range (start after end) is reported as-is rather than silently
   * reordered, matching MUI X — check `validationError` before using the value.
   */
  onChange: (value: DateRangeValue, context: { validationError: DateRangeValidationError }) => void
  /** Preset rail entries — `{ label, getValue }`, the shape the app shortcut hooks already return. */
  presets?: DateRangePreset[]
  min?: Date
  max?: Date
  /**
   * Blocks every day after today. Defaults to `true` here — ranges are reporting
   * windows — where `DatePicker` defaults to `false`.
   */
  disableFuture?: boolean
  /** Closes the popover once both ends are picked. Off by default, as consumers rely on. */
  closeOnSelect?: boolean
  disabled?: boolean
  format?: string
  /** Styles the fields (desktop) or the summary trigger (mobile). */
  className?: string
  /** Applied to the element the popover anchors to, for layout and test hooks. */
  wrapperClassName?: string
  /** Sheet band below `md`; pass `popover` in a modal-band host, or the sheet opens behind it. */
  sheetLayer?: DrawerLayer
  id?: string
  'data-tst'?: string
}

// Sizes the whole input group. On the start field that box also holds the calendar addon, so its
// text has less room than the end field's — enough for the default `dd/MM/yyyy`, but a caller that
// overrides `format` with a longer mask clips the start field first.
const DESKTOP_FIELD_WIDTH = 'w-40'

/** Empty ends read as "no range yet" to the calendar, so they must not survive as `null`. */
function toCalendarRange([start, end]: DateRangeValue) {
  return { from: start ?? undefined, to: end ?? undefined }
}

/**
 * Date range picker: two masked fields over a two-month calendar and a preset
 * rail, in a Base UI popover on desktop and a bottom sheet below `md`.
 *
 * Replaces `@mui/x-date-pickers-pro`'s `DateRangePicker`. The two separate
 * desktop inputs are load-bearing rather than cosmetic — consumers label the
 * pair as one `role="group"` and assert both.
 *
 * The popover anchors to the field row instead of wrapping it in a trigger,
 * which would claim Enter/Space from the inputs; the calendar button is the real
 * `Popover.Trigger`, carrying `aria-haspopup`/`aria-expanded` and giving the grid
 * a keyboard path via Base UI's focus guards.
 */
export function DateRangePicker({
  value,
  onChange,
  presets = [],
  min,
  max,
  disableFuture = true,
  closeOnSelect = false,
  disabled,
  format = 'dd/MM/yyyy',
  className,
  wrapperClassName,
  sheetLayer = 'drawer',
  id,
  'data-tst': dataTst,
}: DateRangePickerProps) {
  const intl = useIntl()
  const isMobile = useMediaQuery('down', 'md')

  // The sheet edits a copy so Cancel can abandon it; the popover writes through.
  const [draft, setDraft] = useState<DateRangeValue>(value)
  // Set between the two clicks that make a range; `null` means the next click
  // starts a new one.
  const [pendingStart, setPendingStart] = useState<Date | null>(null)

  const [start, end] = value

  // Stable for the session: a fresh `new Date()` each render would hand the
  // calendar a new bounds array and end month every keystroke, and let the bound
  // drift by milliseconds mid-session.
  const today = useMemo(() => endOfDay(new Date()), [])
  const upperBound = disableFuture ? (max ?? today) : max

  const startLabel = intl.formatMessage({ id: 'common.dateRangePicker.start' })
  const endLabel = intl.formatMessage({ id: 'common.dateRangePicker.end' })
  const panelLabel = intl.formatMessage({ id: 'common.dateRangePicker.selectDateRange' })

  const popover = useCalendarPopover({ anchorDate: start ?? end, disabled })

  // Both masks live here rather than inside two `DateField`s so the validation
  // pair can be DERIVED every render. Remembering the sibling's error instead
  // meant it outlived the value it described, and re-emitting a stale `maxDate`
  // kept a consumer's submit blocked until that field happened to be retyped.
  const startSegments = useDateSegments({
    value: start,
    format,
    min,
    max: end ?? upperBound,
    disabled,
    onChange: (next, context) => {
      // This field's error comes from the callback, which is a render ahead of
      // `startSegments.validationError`; the sibling did not change, so reading
      // it live is correct.
      commit([next, end], 'field', [context.validationError, endSegments.validationError])
    },
  })

  const endSegments = useDateSegments({
    value: end,
    format,
    min: start ?? min,
    max: upperBound,
    disabled,
    onChange: (next, context) => {
      commit([start, next], 'field', [startSegments.validationError, context.validationError])
    },
  })

  /**
   * The one write path. Errors are always derived from the two masks or from the
   * bounds — never remembered, so they cannot outlive the value they describe.
   *
   * `origin` decides what else the write disturbs:
   *   - `field` — a mask edited itself; touch nothing.
   *   - `calendar` — a day was picked. Any mask left partial must be cleared,
   *     since a `Date | null` of `null` cannot express "half-typed". The
   *     half-made range belongs to `handleCalendarSelect`, which owns it.
   *   - `preset` — the range is replaced outright, so a half-made one goes too.
   */
  function commit(
    next: DateRangeValue,
    origin: 'field' | 'calendar' | 'preset',
    errors?: DateRangeValidationError,
  ) {
    if (origin === 'preset') {
      setPendingStart(null)
    }

    if (origin !== 'field') {
      if (next[0] == null) {
        startSegments.reset()
      }

      if (next[1] == null) {
        endSegments.reset()
      }
    }

    const bounds = { min, max: upperBound }

    onChange(next, {
      validationError: errors ?? [validateBounds(next[0], bounds), validateBounds(next[1], bounds)],
    })
  }

  const handleCalendarSelect = (_range: unknown, triggerDate?: Date) => {
    if (!triggerDate) {
      return
    }

    const commitRange = (next: DateRangeValue) => {
      if (isMobile) {
        setDraft(next)

        return
      }

      commit(next, 'calendar')

      if (closeOnSelect && next[0] && next[1]) {
        popover.setOpen(false)
      }
    }

    if (pendingStart) {
      const next: DateRangeValue =
        triggerDate.getTime() < pendingStart.getTime()
          ? [triggerDate, pendingStart]
          : [pendingStart, triggerDate]

      setPendingStart(null)
      commitRange(next)

      return
    }

    setPendingStart(triggerDate)
    commitRange([triggerDate, null])
  }

  // A finished range with no end is open to now — the "Last 7 days" presets
  // return `[start, null]` so the filter keeps rolling. Paint that end as today
  // so the calendar shows the range the filter will apply; the value itself
  // stays `null`. A half-made range (first tap) keeps showing its start only, and
  // so does a start after today (possible with `disableFuture={false}`).
  const shownRange: DateRangeValue = (() => {
    const [rangeStart, rangeEnd] = isMobile ? draft : value
    const isOpenToNow =
      rangeStart !== null && rangeEnd === null && pendingStart === null && rangeStart <= today

    return isOpenToNow ? [rangeStart, today] : [rangeStart, rangeEnd]
  })()

  const calendar = (
    <Calendar
      maxDate={upperBound}
      minDate={min}
      mode="range"
      month={popover.month}
      numberOfMonths={isMobile ? 1 : 2}
      selected={toCalendarRange(shownRange)}
      // The sheet shows one month, so a trailing day from the next month is noise
      // (Figma leaves those cells empty); two desktop months need them to line up.
      showOutsideDays={!isMobile}
      size={isMobile ? 'medium' : 'small'}
      onMonthChange={popover.setMonth}
      onSelect={handleCalendarSelect}
    />
  )

  if (isMobile) {
    const [shownStart, shownEnd] = shownRange
    const headerFormat = [shownStart, shownEnd].every((date) => !date || isThisYear(date))
      ? 'MMM d'
      : 'MMM d, yy'
    const summary =
      start || end
        ? `${start ? formatDate(start, format) : startLabel} – ${end ? formatDate(end, format) : endLabel}`
        : `${startLabel} – ${endLabel}`
    const { input } = inputVariants({ size: 'medium' })

    return (
      <Drawer.Root
        open={popover.open}
        swipeDirection="down"
        onOpenChange={(next) => {
          if (next) {
            setDraft(value)
            setPendingStart(null)
          }

          popover.setOpen(next)
        }}
      >
        {/* Composes the DS field surface rather than restating its tokens, so the
            trigger cannot drift from `Input`. The control stays the trigger's own
            `<button>`, because Base UI rejects the `<input>` that `Input` renders.
            Its `after:` overlay stretches the button over the whole field, so a
            tap on the calendar icon opens the sheet too. */}
        <InputGroup
          className={cn('relative', className)}
          size="medium"
          startAddon={<CalendarIcon />}
        >
          <Drawer.Trigger
            aria-label={panelLabel}
            className={cn(input(), 'min-w-0 truncate after:absolute after:inset-0')}
            data-slot="input-group-control"
            data-tst={dataTst}
            disabled={disabled}
            id={id}
          >
            {summary}
          </Drawer.Trigger>
        </InputGroup>

        <Drawer.Portal>
          <Drawer.Backdrop layer={sheetLayer} />
          {/* The app's `MobileDrawer` composition (libs/ui cannot import it):
              back chevron + centred `button/large` title, one scrolling body,
              a pinned footer band. Figma PLO-Mobile-Study 694:355345. */}
          <Drawer.Popup
            className="gap-2 overflow-hidden bg-paper-elevation2"
            layer={sheetLayer}
            side="bottom"
            size="lg"
          >
            <div className="flex min-h-12 shrink-0 items-center gap-2 px-2">
              <IconButton
                aria-label={intl.formatMessage({ id: 'common.back' })}
                shape="square"
                size="large"
                onClick={() => {
                  popover.setOpen(false)
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              {/* The spacer mirrors the back button, so the title stays centred and
                  a long translation truncates before it reaches either edge. */}
              <Drawer.Title
                className={cn(typography['button/large'], 'min-w-0 flex-1 truncate text-center')}
              >
                <FormattedMessage id="common.dateRangePicker.title" />
              </Drawer.Title>
              <div className="size-12 shrink-0" />
            </div>

            <Drawer.Content className="flex flex-col gap-4 px-2 pb-4">
              <div className="flex flex-col gap-1 pb-1 pt-2">
                <Typography
                  className="uppercase"
                  color="text.secondary"
                  variant="overline"
                >
                  <FormattedMessage id="common.dateRangePicker.selectDateRange" />
                </Typography>
                <Typography variant="h4">
                  {shownStart ? formatDate(shownStart, headerFormat) : startLabel}
                  {' – '}
                  <span className="text-text-secondary">
                    {shownEnd ? formatDate(shownEnd, headerFormat) : endLabel}
                  </span>
                </Typography>
              </div>

              <DateRangePresets
                horizontal
                items={presets}
                value={draft}
                onSelect={(next) => {
                  // A preset replaces the range outright, so it must also drop a
                  // half-made one — otherwise the next tap completes a range
                  // anchored to the day tapped BEFORE the preset, silently
                  // discarding it. The desktop path gets this from commit(_, 'preset').
                  setPendingStart(null)
                  setDraft(next)
                }}
              />

              <div className="flex justify-center">{calendar}</div>
            </Drawer.Content>

            <Drawer.Footer className="items-center justify-start gap-2 rounded-t-md bg-paper-elevation5 p-2 shadow-8">
              <Button
                className="flex-1"
                color="secondary"
                size="large"
                variant="text"
                onClick={() => {
                  popover.setOpen(false)
                }}
              >
                <FormattedMessage id="common.cancel" />
              </Button>
              <Button
                className="flex-1"
                color="primary"
                size="large"
                variant="contained"
                onClick={() => {
                  commit(draft, 'preset')
                  popover.setOpen(false)
                }}
              >
                <FormattedMessage id="common.confirm" />
              </Button>
            </Drawer.Footer>
          </Drawer.Popup>
        </Drawer.Portal>
      </Drawer.Root>
    )
  }

  return (
    <Popover.Root {...popover.rootProps}>
      <div
        {...popover.anchorProps}
        className={cn('flex items-center gap-2', wrapperClassName)}
        data-tst={dataTst}
        id={id}
      >
        <DateFieldInput
          aria-label={startLabel}
          className={cn(DESKTOP_FIELD_WIDTH, className)}
          disabled={disabled}
          segments={startSegments}
          startAddon={
            <Popover.Trigger
              disabled={disabled}
              render={
                <IconButton
                  aria-label={panelLabel}
                  color="secondary"
                  size="small"
                />
              }
            >
              <CalendarIcon />
            </Popover.Trigger>
          }
        />
        <span
          aria-hidden
          className="text-text-secondary"
        >
          –
        </span>
        <DateFieldInput
          aria-label={endLabel}
          className={cn(DESKTOP_FIELD_WIDTH, className)}
          disabled={disabled}
          segments={endSegments}
        />
      </div>

      <Popover.Portal>
        <Popover.Positioner
          align="end"
          anchor={popover.anchorRef}
        >
          <Popover.Popup
            ref={popover.popupRef}
            aria-label={panelLabel}
            // Focus stays in the fields so typing continues; the trigger's focus
            // guards are what make the grid reachable by keyboard.
            initialFocus={false}
          >
            <div className="flex">
              <DateRangePresets
                items={presets}
                value={value}
                onSelect={(next) => {
                  commit(next, 'preset')
                }}
              />
              {calendar}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
